import {
  AppShell,
  Sidebar,
  SidebarItem,
  SidebarSection,
  StatusDot,
  TopBar,
  UserMenu,
} from '@hkdevs/design-system';
import { useState, type ReactNode } from 'react';
import type { Me, Role } from '../api/queries/me';
import type { NavCounts } from '../api/queries/navCounts';
import type { Tenant } from '../api/queries/tenant';
import { navFor, PAGES, type NavItem, type PageKey } from './pages';
import { RouterLink } from './RouterLink';

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrator',
  operator: 'Operator',
  supervisor: 'Supervisor',
  rep: 'Sales Rep',
  rep_intern: 'Rep intern',
  pay_viewer: 'Pay viewer',
};

/** The role the menu and the sidebar's footer name: the most senior one held. */
export function roleLabel(roles: readonly Role[]): string {
  const role = (['admin', 'pay_viewer', 'supervisor', 'rep', 'rep_intern'] as const).find((r) =>
    roles.includes(r),
  );
  return role ? ROLE_LABELS[role] : 'Signed in';
}

/** Where the sidebar's collapsed state is kept (ADR 0023). */
export const COLLAPSED_KEY = 'pc.sidebarCollapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  } catch {
    // Private mode or blocked storage: the state just isn't remembered.
  }
}

export interface SignedInShellProps {
  me: Me;
  features: Tenant['features'];
  /** Undefined while loading or when they failed: the pills are left out. */
  counts: NavCounts | undefined;
  /** The current page's nav item, or null (an error page). */
  active: PageKey | null;
  onSignOut: () => void;
  /** Above the content: the impersonation and notices banners (App A3). */
  banner?: ReactNode;
  children: ReactNode;
}

/**
 * The signed-in frame, composed as the App shell frame story (pc-dbb.1): the top bar
 * with the user menu, the sidebar for the user's sections, and the page.
 */
export function SignedInShell({
  me,
  features,
  counts,
  active,
  onSignOut,
  banner,
  children,
}: SignedInShellProps) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // Viewing as someone (ADR 0017): the nav is theirs; the menu and footer stay the real
  // user's (an admin or a pay viewer, ADR 0028).
  const viewer = me.impersonation?.realUser;
  const role = roleLabel(viewer ? viewer.roles : me.user.roles);
  const item = ({ key, count }: NavItem) => {
    const page = PAGES[key];
    return (
      <SidebarItem
        key={key}
        as={RouterLink}
        href={page.path}
        icon={page.icon}
        active={key === active}
        {...(count ? { count, countLabel: `${count} pending` } : {})}
      >
        {page.label ?? page.title}
      </SidebarItem>
    );
  };
  return (
    <AppShell
      sidebarCollapsed={collapsed}
      onSidebarCollapsedChange={(next) => {
        setCollapsed(next);
        writeCollapsed(next);
      }}
      topbar={
        <TopBar
          actions={
            <UserMenu
              name={viewer ? viewer.name : me.user.name}
              {...(!viewer && { email: me.user.email })}
              roleLabel={role}
              {...(!viewer && me.user.pictureUrl ? { avatarSrc: me.user.pictureUrl } : {})}
              onSignOut={onSignOut}
            />
          }
        />
      }
      sidebar={
        <Sidebar footer={<StatusDot tone="success">Signed in as {role}</StatusDot>}>
          {navFor(me.sections, features, counts).map((entry) =>
            'items' in entry ? (
              <SidebarSection key={entry.label} label={entry.label} icon={entry.icon}>
                {entry.items.map(item)}
              </SidebarSection>
            ) : (
              item(entry)
            ),
          )}
        </Sidebar>
      }
      banner={banner}
    >
      {children}
    </AppShell>
  );
}
